import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { MDXProvider } from '@mdx-js/react';
import { Calendar, Clock } from 'lucide-react';
import { blogPosts } from '../content/blog';
import Layout from '../components/Layout';
import TableOfContents from '../components/TableOfContents';
import { extractToc } from '../utils/extractToc';
import { setupThemeListener } from '../utils/highlightTheme';
import '../styles/markdown.css';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const [mobileIsOpen, setMobileIsOpen] = useState(false);

  // Extract TOC from post content (before early return to follow React rules)
  const toc = useMemo(() => {
    if (!post) return [];
    return extractToc(post.content);
  }, [post]);

  // Set up dynamic syntax highlighting theme
  useEffect(() => {
    const cleanup = setupThemeListener();
    return cleanup;
  }, []);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const handleToggleToc = () => {
    setMobileIsOpen(!mobileIsOpen);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const estimateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };

  return (
    <Layout showTocButton={true} onToggleToc={handleToggleToc}>
      <TableOfContents toc={toc} mobileIsOpen={mobileIsOpen} onMobileToggle={handleToggleToc} />

      <div className="mx-auto max-w-5xl px-4 md:px-8 xl:px-24 2xl:px-32">
        <article className="prose dark:prose-invert prose-lg max-w-none">
          <header className="mb-8 pb-8 border-b border-gray-300 dark:border-gray-800">
            <h1 className="text-4xl tracking-tight text-black dark:text-gray-200 sm:text-5xl mb-4">
              {post.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-500 mb-4">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {estimateReadTime(post.content)} min read
              </span>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="rounded-full bg-blue-600/10 dark:bg-blue-400/10 px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          <div className="markdown-content">
            <MDXProvider>
              <post.component />
            </MDXProvider>
          </div>
        </article>
      </div>
    </Layout>
  );
};

export default BlogPost;

